import { BoolNode } from 'src/services/common.types';
import { readEventStream, streamAuthHeaders } from 'src/services/sse';

import { IOrgCandidateListItem } from './candidates.types';

/**
 * The streamed advanced search (JT1-1802), read from `POST /my/candidates/search/stream`.
 *
 * The backend walks the organization in blocks and sends each block's matches as they are found,
 * so the first results arrive in about a second where the one-shot search could run past its 20s
 * limit and return nothing. Shapes mirror `StreamEvent` in the backend's `keyset-search.ts`.
 */
export type CandidateSearchEvent =
  | { type: 'rows'; items: IOrgCandidateListItem[] }
  | { type: 'progress'; checked: number; total: number; found: number }
  /** `partial`: some candidates could not be checked, so the list may be incomplete (JT1-1804). */
  | { type: 'done'; found: number; capped: boolean; partial?: boolean }
  /** The server is busy; the search starts when a slot frees. */
  | { type: 'queued' }
  | { type: 'error'; status?: number; message?: string };

const malformedError = (data: string): CandidateSearchEvent => ({ type: 'error', message: data });

/** The keyword box's search, as GET /my/candidates/list takes it (JT1-1805). */
export interface CandidateKeywordQuery {
  search_query: string;
  column_filters?: Record<string, string>;
  list_id?: string;
}

/**
 * The keyword box's streamed search, `GET /my/candidates/list/stream` (JT1-1805): the plain list's
 * filters, phrase first then per word, delivered as the same events as the advanced search.
 */
export async function streamCandidateKeywordSearch(
  { query, signal }: { query: CandidateKeywordQuery; signal?: AbortSignal },
  onEvent: (event: CandidateSearchEvent) => void,
): Promise<void> {
  const params = new URLSearchParams({ search_query: query.search_query });
  if (query.list_id) params.set('list_id', query.list_id);
  if (query.column_filters && Object.keys(query.column_filters).length) {
    params.set('column_filters', JSON.stringify(query.column_filters));
  }

  const response = await fetch(`${import.meta.env.VITE_BACKEND_URL}/my/candidates/list/stream?${params}`, {
    headers: streamAuthHeaders(),
    signal,
  });
  await readCandidateStream(response, signal, onEvent);
}

async function readCandidateStream(
  response: Response,
  signal: AbortSignal | undefined,
  onEvent: (event: CandidateSearchEvent) => void,
): Promise<void> {
  if (!response.ok || !response.body) {
    onEvent({ type: 'error', status: response.status });
    return;
  }

  const finished = await readEventStream<CandidateSearchEvent>(
    response,
    onEvent,
    (event) => event.type === 'done' || event.type === 'error',
    malformedError,
  );

  if (!finished && !signal?.aborted) onEvent({ type: 'error', status: 0 });
}

/**
 * Runs the search and calls `onEvent` for each event until it ends. A refusal before the stream
 * opens, and a stream that closes without `done`, both arrive as an `error` event. Aborting
 * `signal` cancels the request; the server then stops searching.
 */
export async function streamCandidateSearch(
  { dsl, columnFilters, signal }: { dsl: BoolNode; columnFilters?: Record<string, string>; signal?: AbortSignal },
  onEvent: (event: CandidateSearchEvent) => void,
): Promise<void> {
  const params = columnFilters && Object.keys(columnFilters).length
    ? `?${new URLSearchParams({ column_filters: JSON.stringify(columnFilters) })}`
    : '';

  const response = await fetch(`${import.meta.env.VITE_BACKEND_URL}/my/candidates/search/stream${params}`, {
    method: 'POST',
    headers: { ...streamAuthHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify(dsl),
    signal,
  });
  await readCandidateStream(response, signal, onEvent);
}
